import React from 'react';
import { Box, Flex, Text, Checkbox, Divider, Badge, Icon, Switch, Button } from '@chakra-ui/react';
import { FiAlertCircle, FiCheckCircle } from 'react-icons/fi';
import dayjs from 'dayjs';
import MatriculaSelector from './MatriculaSelector';
import OrdinalsPicker from './OrdinalsPicker';
import MonthsPicker from './MonthsPicker';
import SubjectsInfoAccordion from './SubjectsInfoAccordion';
import { CODE_COPY } from './constants';

function fmtDate(iso) {
  if (!iso) return '—';
  const d = dayjs(iso);
  return d.isValid() ? d.format('DD-MM-YYYY') : '—';
}

function fmtMoney(n) {
  if (n == null) return '—';
  return `$${Number(n).toLocaleString('es-CO')} COP`;
}

/**
 * Una tarjeta por pago huérfano. Toggle "incluir" apaga visualmente y excluye
 * del batch. Cuando el pago está persistido (status saved/failed) se congela
 * la interacción.
 */
export default function PaymentCard({
  card,
  enrollments,
  isSemesterBased,
  subjectsTotal,
  reservedOrdinals,
  reservedMonths,
  blockedOrdinalsInDb,
  monthIdByNumber,
  onSelectCandidate,
  onToggleOrdinal,
  onToggleMonth,
  onToggleInclude,
  onToggleSkipOrdinals,
  onUnlink,
  isUnlinking = false,
}) {
  const { payment, enrollmentId, ordinals, months, skipOrdinals, included, status, error } = card;
  const conceptName = (Array.isArray(payment.other_payments) && payment.other_payments[0]?.name) || null;
  const isFrozen = status === 'saved' || status === 'failed';
  const dimmed = !included && status !== 'saved';

  const currentEnrollment = enrollments.find((e) => e.id === enrollmentId);
  // Cuando el pago ya venía linkeado a un enrollment que existe en la lista,
  // dejar que Cindy lo cambie si quiere (multi-matrícula). Cuando no existe
  // (huérfano puro), la lista completa está disponible.
  const candidates = enrollments;

  const borderColor = status === 'saved'
    ? 'green.200'
    : status === 'failed'
      ? 'red.200'
      : included
        ? 'gray.200'
        : 'gray.100';
  // Nota: si el pago está 'saved' pero originalmente venía vinculado desde BD,
  // lo pintamos con acento morado; los recién guardados en este batch quedan verdes.

  const bg = status === 'saved'
    ? 'green.50'
    : status === 'failed'
      ? 'red.50'
      : 'white';

  return (
    <Box
      borderWidth="1px"
      borderColor={borderColor}
      bg={bg}
      borderRadius="8px"
      p={4}
      opacity={dimmed ? 0.65 : 1}
      transition="opacity 0.2s"
    >
      {/* Header: check incluir + info del pago + status badge */}
      <Flex align="flex-start" gap={3}>
        {status !== 'saved' && (
          <Checkbox
            isChecked={included}
            isDisabled={isFrozen}
            onChange={() => onToggleInclude(payment.id)}
            mt={0.5}
          />
        )}
        <Box flex="1">
          <Flex align="center" gap={2} flexWrap="wrap">
            <Text fontSize="14px" fontWeight="700" color="gray.800">
              Pago #{payment.id}
            </Text>
            <Text fontSize="12px" color="gray.500">
              · {fmtDate(payment.payment_date)}
            </Text>
            <Text fontSize="12px" color="gray.500">
              · {fmtMoney(payment.amount)}
            </Text>
            {payment.method && (
              <Text fontSize="12px" color="gray.500">
                · {payment.method}
              </Text>
            )}
            {payment.type && (
              <Badge colorScheme="gray" fontSize="10px" textTransform="none">
                {payment.type === 'subjects' ? 'Compra materia' : 'Otros pagos'}
              </Badge>
            )}
            {status === 'saved' && (
              <Badge colorScheme="green" fontSize="10px" px={2}>
                <Flex align="center" gap={1}>
                  <Icon as={FiCheckCircle} boxSize="10px" />
                  Vinculado
                </Flex>
              </Badge>
            )}
            {status === 'failed' && (
              <Badge colorScheme="red" fontSize="10px" px={2}>
                <Flex align="center" gap={1}>
                  <Icon as={FiAlertCircle} boxSize="10px" />
                  No se pudo guardar
                </Flex>
              </Badge>
            )}
          </Flex>
          {(conceptName || payment.description) && (
            <Box fontSize="12px" color="gray.600" mt={1}>
              {conceptName && (
                <>Concepto: <Box as="span" fontWeight="600" color="gray.700">{conceptName}</Box></>
              )}
              {conceptName && payment.description && ' · '}
              {payment.description && (
                <Box as="span" fontStyle="italic" color="gray.500">"{payment.description}"</Box>
              )}
            </Box>
          )}
        </Box>
      </Flex>

      {/* Vista compacta para pagos ya vinculados en BD: cantidades pintadas o badge "no cubre" + botón Desvincular */}
      {status === 'saved' && payment.linked && (
        <Box mt={3} pt={3} borderTopWidth="1px" borderColor="green.100">
          <Flex align="center" justify="space-between" gap={2} flexWrap="wrap">
            <Box>
              <Text fontSize="11px" color="gray.500" fontWeight="600" letterSpacing="0.05em">
                {skipOrdinals ? 'ESTADO' : 'CANTIDADES VINCULADAS'}
              </Text>
              {skipOrdinals ? (
                <Flex align="center" gap={2} mt={1}>
                  <Box
                    px={2}
                    py={0.5}
                    borderRadius="4px"
                    bg="#FEF3C7"
                    color="#92400E"
                    fontSize="11px"
                    fontWeight="600"
                  >
                    No cubre materias
                  </Box>
                  <Text fontSize="12px" color="gray.500" fontStyle="italic">
                    Vinculado a la matrícula, sin aportar cantidades.
                  </Text>
                </Flex>
              ) : ordinals && ordinals.length > 0 ? (
                <Flex gap={1} mt={1} flexWrap="wrap">
                  {ordinals.map((n) => (
                    <Box
                      key={n}
                      px={2}
                      py={0.5}
                      borderRadius="4px"
                      bg="#EDE9FE"
                      color="#5B21B6"
                      fontSize="11px"
                      fontWeight="600"
                    >
                      Materia {n}
                    </Box>
                  ))}
                </Flex>
              ) : (
                <Text fontSize="12px" color="gray.500" mt={1} fontStyle="italic">
                  Sin detalle de cantidades.
                </Text>
              )}
            </Box>
            {onUnlink && (
              <Button
                size="xs"
                variant="outline"
                colorScheme="red"
                onClick={() => onUnlink(payment.id)}
                isLoading={isUnlinking}
                isDisabled={isUnlinking}
              >
                Desvincular
              </Button>
            )}
          </Flex>
        </Box>
      )}

      {/* Cuerpo: solo si está incluido y no está saved */}
      {included && status !== 'saved' && (
        <Box mt={4}>
          <Divider mb={4} />

          <Text fontSize="11px" color="gray.500" fontWeight="600" mb={2} letterSpacing="0.05em">
            MATRÍCULA
          </Text>
          <MatriculaSelector
            enrollments={candidates}
            selectedId={enrollmentId}
            suggestedId={payment.suggestedEnrollmentId}
            onSelect={(id) => onSelectCandidate(payment.id, id)}
            disabled={isFrozen}
          />

          <Box mt={4}>
            {isSemesterBased ? (
              <>
                {onToggleSkipOrdinals && (
                  <Flex align="center" gap={2} mb={3}>
                    <Switch
                      isChecked={!!skipOrdinals}
                      onChange={() => onToggleSkipOrdinals(payment.id)}
                      isDisabled={isFrozen}
                      colorScheme="purple"
                      size="sm"
                    />
                    <Box flex="1">
                      <Text fontSize="12px" fontWeight="600" color={skipOrdinals ? 'purple.700' : 'gray.700'}>
                        Este pago no cubre materias
                      </Text>
                      <Text fontSize="11px" color="gray.500">
                        Se vinculará a la matrícula pero no aportará cantidades al reporte (ej. Servicio Social, Derecho de Grado).
                      </Text>
                    </Box>
                  </Flex>
                )}
                {!skipOrdinals && (
                  <OrdinalsPicker
                    subjectsTotal={subjectsTotal}
                    selected={ordinals}
                    reservedByPeers={reservedOrdinals}
                    blockedInDb={blockedOrdinalsInDb || null}
                    onToggle={(n) => onToggleOrdinal(payment.id, n)}
                    disabled={isFrozen}
                  />
                )}
              </>
            ) : (
              <MonthsPicker
                selected={months}
                reservedByPeers={reservedMonths}
                blockedInDb={null}
                monthIdByNumber={monthIdByNumber}
                onToggle={(id) => onToggleMonth(payment.id, id)}
                disabled={isFrozen}
              />
            )}
          </Box>

          <Box mt={4}>
            <SubjectsInfoAccordion
              subjects={payment.subjects}
              otherPayments={payment.other_payments}
            />
          </Box>
        </Box>
      )}

      {/* Bloque de error inline si falló */}
      {status === 'failed' && error && (
        <Box mt={3} borderTopWidth="1px" borderColor="red.100" pt={3}>
          <Flex align="flex-start" gap={2}>
            <Icon as={FiAlertCircle} color="red.500" boxSize="16px" mt="1px" />
            <Box>
              <Text fontSize="12px" color="red.700" fontWeight="600">
                {CODE_COPY[error.code] || error.message || 'No se pudo guardar'}
              </Text>
              {error.conflict?.ordinal && (
                <Text fontSize="11px" color="red.600" mt={0.5}>
                  La <b>Materia {error.conflict.ordinal}</b> fue tomada por el
                  Pago #{error.conflict.wonBy?.payment_id ?? '—'} en otra sesión.
                  Ajusta y reintenta.
                </Text>
              )}
            </Box>
          </Flex>
        </Box>
      )}
    </Box>
  );
}
